(function() {
    'use strict';

    if (!window.cowHuntSettings || !window.cowHuntSettings.enabled) {
        return;
    }

    const GAME_SETTINGS = {
        totalCows: window.cowHuntSettings.totalCows || 5,
        buttonText: window.cowHuntSettings.buttonText || '🐄 Start Cow Hunt!',
        cow1Page: window.cowHuntSettings.cow1Page || '/',
        cow2Page: window.cowHuntSettings.cow2Page || '/products',
        cow3Page: window.cowHuntSettings.cow3Page || '/collections',
        cow4Page: window.cowHuntSettings.cow4Page || '/pages/about',
        cow5Pages: window.cowHuntSettings.cow5Pages || ['/pages/default']
    };

    const COW_COLORS = {
        1: '#F2F72F',
        2: '#F47B0F',
        3: '#7AC943',
        4: '#5DBAED',
        5: '#CE3245'
    };

    const CowHuntGame = {
        state: {
            active: false,
            cowsFound: [],
            totalCows: GAME_SETTINGS.totalCows,
            gameSession: null,
            securityKey: null,
            trackerMinimized: false,
            lastFoundCowColor: null,
            cow5SelectedPage: null,
            currentPageCows: [],
            gameCompleted: false,
            lastWindowSize: { width: 0, height: 0 },
            currentPath: window.location.pathname,
            pageCheckInterval: null
        },

        hashString: function(str) {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                const char = str.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash;
            }
            return Math.abs(hash);
        },

        generateRandomPositions: function() {
            const positions = [];
            const usedAreas = [];
            
            const body = document.body;
            const html = document.documentElement;
            
            const pageHeight = Math.max(
                body.scrollHeight,
                body.offsetHeight,
                html.clientHeight,
                html.scrollHeight,
                html.offsetHeight
            );
            
            const pageWidth = Math.max(
                body.scrollWidth,
                body.offsetWidth,
                html.clientWidth,
                html.scrollWidth,
                html.offsetWidth
            );
            
            const minWidth = Math.max(pageWidth, window.innerWidth);
            const minHeight = Math.max(pageHeight, window.innerHeight);
            
            for (let i = 0; i < this.state.totalCows; i++) {
                let position;
                let attempts = 0;
                
                do {
                    const cowSize = 60;
                    const margin = 30;
                    
                    const topPixels = Math.random() * (minHeight - cowSize - (margin * 2)) + margin;
                    const leftPixels = Math.random() * (minWidth - cowSize - (margin * 2)) + margin;
                    
                    position = {
                        top: topPixels + 'px',
                        left: leftPixels + 'px'
                    };
                    attempts++;
                } while (this.isPositionTooClose(position, usedAreas) && attempts < 50);
                
                positions.push(position);
                usedAreas.push(position);
            }
            
            return positions;
        },

        isPositionTooClose: function(newPos, existingPositions) {
            const minDistance = 150;
            const newTop = parseFloat(newPos.top);
            const newLeft = parseFloat(newPos.left);
            
            return existingPositions.some(pos => {
                const existingTop = parseFloat(pos.top);
                const existingLeft = parseFloat(pos.left);
                
                const distance = Math.sqrt(
                    Math.pow(newTop - existingTop, 2) + 
                    Math.pow(newLeft - existingLeft, 2)
                );
                
                return distance < minDistance;
            });
        },

        getCowPage: function(cowId) {
            switch(cowId) {
                case 1: return GAME_SETTINGS.cow1Page;
                case 2: return GAME_SETTINGS.cow2Page;
                case 3: return GAME_SETTINGS.cow3Page;
                case 4: return GAME_SETTINGS.cow4Page;
                case 5: return this.state.cow5SelectedPage;
                default: return null;
            }
        },

        selectRandomPageForCow5: function() {
            const randomIndex = Math.floor(Math.random() * GAME_SETTINGS.cow5Pages.length);
            this.state.cow5SelectedPage = GAME_SETTINGS.cow5Pages[randomIndex];
        },

        shouldShowCowOnPage: function(cowId, currentPage) {
            const assignedPage = this.getCowPage(cowId);
            if (!assignedPage) return false;
            
            return currentPage === assignedPage || 
                   (assignedPage !== '/' && currentPage.startsWith(assignedPage));
        },

        storeGameData: function(data) {
            try {
                const dataToStore = {
                    active: data.active !== undefined ? data.active : this.state.active,
                    cowsFound: data.cowsFound || this.state.cowsFound,
                    totalCows: data.totalCows || this.state.totalCows,
                    gameSession: data.gameSession || this.state.gameSession,
                    timestamp: Date.now(),
                    trackerMinimized: this.state.trackerMinimized,
                    lastFoundCowColor: this.state.lastFoundCowColor || data.lastFoundCowColor || null,
                    gameCompleted: this.state.gameCompleted || data.gameCompleted || false,
                    validation: this.generateValidationHash(data.cowsFound || this.state.cowsFound, data.gameSession || this.state.gameSession)
                };
                sessionStorage.setItem('cowHuntGame', JSON.stringify(dataToStore));
            } catch (e) {
                console.warn('SessionStorage not available');
            }
        },

        generateValidationHash: function(cowsFound, gameSession) {
            if (!cowsFound || !gameSession) return null;
            
            const secret = "cowHuntValidation2024";
            const dataString = cowsFound.sort().join(',') + gameSession + secret;
            return this.hashString(dataString).toString();
        },

        validateStoredData: function(data) {
            if (!data || !data.validation || !data.cowsFound || !data.gameSession) {
                return false;
            }
            
            const expectedHash = this.generateValidationHash(data.cowsFound, data.gameSession);
            return data.validation === expectedHash;
        },

        loadGameData: function() {
            try {
                const data = sessionStorage.getItem('cowHuntGame');
                if (!data) return null;
                
                const parsed = JSON.parse(data);
                
                if (parsed.timestamp && (Date.now() - parsed.timestamp) > 86400000) {
                    this.resetGame();
                    return null;
                }
                
                if (!this.validateStoredData(parsed)) {
                    console.warn('Invalid game data detected, resetting game');
                    this.resetGame();
                    return null;
                }
                
                if (parsed.cowsFound && parsed.cowsFound.length > GAME_SETTINGS.totalCows) {
                    console.warn('Invalid cow count detected, resetting game');
                    this.resetGame();
                    return null;
                }
                
                if (parsed.cowsFound && parsed.cowsFound.some(id => id < 1 || id > GAME_SETTINGS.totalCows)) {
                    console.warn('Invalid cow ID detected, resetting game');
                    this.resetGame();
                    return null;
                }
                
                if (parsed.hasOwnProperty('trackerMinimized')) {
                    this.state.trackerMinimized = parsed.trackerMinimized;
                } else {
                    this.state.trackerMinimized = true;
                }

                if (parsed.hasOwnProperty('lastFoundCowColor')) {
                    this.state.lastFoundCowColor = parsed.lastFoundCowColor;
                }

                if (parsed.hasOwnProperty('gameCompleted')) {
                    this.state.gameCompleted = parsed.gameCompleted;
                }
                
                return parsed;
            } catch (e) {
                console.warn('Error loading game data, resetting game');
                this.resetGame();
                return null;
            }
        },

        startPageMonitoring: function() {
            if (this.state.pageCheckInterval) {
                clearInterval(this.state.pageCheckInterval);
            }
            
            this.state.pageCheckInterval = setInterval(() => {
                const gameData = this.loadGameData();
                if (gameData && (gameData.active || gameData.gameCompleted)) {
                    const currentPath = window.location.pathname;
                    if (currentPath !== this.state.currentPath) {
                        this.state.currentPath = currentPath;
                        this.handlePageNavigation();
                    }
                }
            }, 100);
        },

        stopPageMonitoring: function() {
            if (this.state.pageCheckInterval) {
                clearInterval(this.state.pageCheckInterval);
                this.state.pageCheckInterval = null;
            }
        },

        createSuccessModal: function() {
            const existingModal = document.getElementById('cowHuntSuccessModal');
            if (existingModal) {
                existingModal.remove();
            }

            const modal = document.createElement('div');
            modal.id = 'cowHuntSuccessModal';
            modal.className = 'cow-hunt-modal';
            modal.style.display = 'block';
            modal.style.zIndex = '2000';

            const modalContent = document.createElement('div');
            modalContent.className = 'cow-hunt-modal-content cow-hunt-success-content';

            const closeBtn = document.createElement('span');
            closeBtn.className = 'cow-hunt-close';
            closeBtn.innerHTML = '&times;';
            closeBtn.onclick = () => this.closeSuccessModal();

            if (window.cowHuntSettings && window.cowHuntSettings.successImage) {
                const successImg = document.createElement('img');
                successImg.src = window.cowHuntSettings.successImage;
                successImg.alt = 'Success Cow Hunt';
                successImg.className = 'cow-hunt-success-image';
                successImg.style.cssText = 'max-width: 100%; height: auto; border-radius: 12px;';
                modalContent.appendChild(successImg);
            } else {
                const successText = document.createElement('p');
                successText.style.textAlign = 'center';
                successText.innerHTML = '🎉 You found all the cows!';
                modalContent.appendChild(successText);
            }

            modalContent.appendChild(closeBtn);
            modal.appendChild(modalContent);
            document.body.appendChild(modal);

            modal.addEventListener('click', (e) => {
                if (e.target === modal) {
                    this.closeSuccessModal();
                }
            });
        },

        closeSuccessModal: function() {
            const modal = document.getElementById('cowHuntSuccessModal');
            if (modal) {
                modal.remove();
            }
            this.resetGame();
        },

        handlePageNavigation: function() {
            const gameData = this.refreshTrackerFromStorage();
            
            if (!gameData || !gameData.active) return;
            
            if (gameData.gameCompleted) {
                this.state.gameCompleted = true;
                this.state.active = false;
                this.createSuccessModal();
                return;
            }
            
            if (!this.state.active || this.state.gameCompleted) return;
            
            this.clearExistingCows();
            
            const positions = this.generateRandomPositions();
            const currentPage = window.location.pathname;
            
            this.state.currentPageCows = positions.map((pos, index) => ({
                id: index + 1,
                position: pos,
                found: this.state.cowsFound.includes(index + 1)
            }));
            
            this.placeCowsForCurrentPage(this.state.currentPageCows, currentPage);
        },

        refreshTrackerFromStorage: function() {
            const gameData = this.loadGameData();
            if (gameData) {
                this.state.cowsFound = gameData.cowsFound || [];
                this.state.lastFoundCowColor = gameData.lastFoundCowColor || null;
                this.state.trackerMinimized = gameData.trackerMinimized !== false;
                this.state.active = gameData.active;
                this.state.gameCompleted = gameData.gameCompleted || false;
                
                if (gameData.active || gameData.gameCompleted) {
                    this.state.cowsFound.forEach(cowId => this.updateCowVisual(cowId));
                    this.updateCounter();
                    this.restoreTrackerState();
                }
            }
            return gameData;
        },

        handleResize: function() {
            if (!this.state.active || this.state.gameCompleted) return;(function() {
    'use strict';

    if (!window.cowHuntSettings || !window.cowHuntSettings.enabled) {
        return;
    }

    const GAME_SETTINGS = {
        totalCows: window.cowHuntSettings.totalCows || 5,
        buttonText: window.cowHuntSettings.buttonText || '🐄 Start Cow Hunt!',
        cow1Page: window.cowHuntSettings.cow1Page || '/',
        cow2Page: window.cowHuntSettings.cow2Page || '/products',
        cow3Page: window.cowHuntSettings.cow3Page || '/collections',
        cow4Page: window.cowHuntSettings.cow4Page || '/pages/about',
        cow5Pages: window.cowHuntSettings.cow5Pages || ['/pages/default']
    };

    const COW_COLORS = {
        1: '#F2F72F',
        2: '#F47B0F',
        3: '#7AC943',
        4: '#5DBAED',
        5: '#CE3245'
    };

    const CowHuntGame = {
        state: {
            active: false,
            cowsFound: [],
            totalCows: GAME_SETTINGS.totalCows,
            gameSession: null,
            securityKey: null,
            trackerMinimized: false,
            lastFoundCowColor: null,
            cow5SelectedPage: null,
            currentPageCows: [],
            gameCompleted: false,
            lastWindowSize: { width: 0, height: 0 },
            currentPath: window.location.pathname,
            pageCheckInterval: null
        },

        hashString: function(str) {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                const char = str.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash;
            }
            return Math.abs(hash);
        },

        generateRandomPositions: function() {
            const positions = [];
            const usedAreas = [];
            
            const body = document.body;
            const html = document.documentElement;
            
            const pageHeight = Math.max(
                body.scrollHeight,
                body.offsetHeight,
                html.clientHeight,
                html.scrollHeight,
                html.offsetHeight
            );
            
            const pageWidth = Math.max(
                body.scrollWidth,
                body.offsetWidth,
                html.clientWidth,
                html.scrollWidth,
                html.offsetWidth
            );
            
            const minWidth = Math.max(pageWidth, window.innerWidth);
            const minHeight = Math.max(pageHeight, window.innerHeight);
            
            for (let i = 0; i < this.state.totalCows; i++) {
                let position;
                let attempts = 0;
                
                do {
                    const cowSize = 60;
                    const margin = 30;
                    const headerExclusion = 150;
                    
                    const topPixels = Math.random() * (minHeight - cowSize - (margin * 2) - headerExclusion) + margin + headerExclusion;
                    const leftPixels = Math.random() * (minWidth - cowSize - (margin * 2)) + margin;
                    
                    position = {
                        top: topPixels + 'px',
                        left: leftPixels + 'px'
                    };
                    attempts++;
                } while (this.isPositionTooClose(position, usedAreas) && attempts < 50);
                
                positions.push(position);
                usedAreas.push(position);
            }
            
            return positions;
        },

        isPositionTooClose: function(newPos, existingPositions) {
            const minDistance = 150;
            const newTop = parseFloat(newPos.top);
            const newLeft = parseFloat(newPos.left);
            
            return existingPositions.some(pos => {
                const existingTop = parseFloat(pos.top);
                const existingLeft = parseFloat(pos.left);
                
                const distance = Math.sqrt(
                    Math.pow(newTop - existingTop, 2) + 
                    Math.pow(newLeft - existingLeft, 2)
                );
                
                return distance < minDistance;
            });
        },

        getCowPage: function(cowId) {
            switch(cowId) {
                case 1: return GAME_SETTINGS.cow1Page;
                case 2: return GAME_SETTINGS.cow2Page;
                case 3: return GAME_SETTINGS.cow3Page;
                case 4: return GAME_SETTINGS.cow4Page;
                case 5: return this.state.cow5SelectedPage;
                default: return null;
            }
        },

        selectRandomPageForCow5: function() {
            const randomIndex = Math.floor(Math.random() * GAME_SETTINGS.cow5Pages.length);
            this.state.cow5SelectedPage = GAME_SETTINGS.cow5Pages[randomIndex];
        },

        shouldShowCowOnPage: function(cowId, currentPage) {
            const assignedPage = this.getCowPage(cowId);
            if (!assignedPage) return false;
            
            return currentPage === assignedPage || 
                   (assignedPage !== '/' && currentPage.startsWith(assignedPage));
        },

        storeGameData: function(data) {
            try {
                const dataToStore = {
                    active: data.active !== undefined ? data.active : this.state.active,
                    cowsFound: data.cowsFound || this.state.cowsFound,
                    totalCows: data.totalCows || this.state.totalCows,
                    gameSession: data.gameSession || this.state.gameSession,
                    timestamp: Date.now(),
                    trackerMinimized: this.state.trackerMinimized,
                    lastFoundCowColor: this.state.lastFoundCowColor || data.lastFoundCowColor || null,
                    gameCompleted: this.state.gameCompleted || data.gameCompleted || false,
                    validation: this.generateValidationHash(data.cowsFound || this.state.cowsFound, data.gameSession || this.state.gameSession)
                };
                sessionStorage.setItem('cowHuntGame', JSON.stringify(dataToStore));
            } catch (e) {
                console.warn('SessionStorage not available');
            }
        },

        generateValidationHash: function(cowsFound, gameSession) {
            if (!cowsFound || !gameSession) return null;
            
            const secret = "cowHuntValidation2024";
            const dataString = cowsFound.sort().join(',') + gameSession + secret;
            return this.hashString(dataString).toString();
        },

        validateStoredData: function(data) {
            if (!data || !data.validation || !data.cowsFound || !data.gameSession) {
                return false;
            }
            
            const expectedHash = this.generateValidationHash(data.cowsFound, data.gameSession);
            return data.validation === expectedHash;
        },

        loadGameData: function() {
            try {
                const data = sessionStorage.getItem('cowHuntGame');
                if (!data) return null;
                
                const parsed = JSON.parse(data);
                
                if (parsed.timestamp && (Date.now() - parsed.timestamp) > 86400000) {
                    this.resetGame();
                    return null;
                }
                
                if (!this.validateStoredData(parsed)) {
                    console.warn('Invalid game data detected, resetting game');
                    this.resetGame();
                    return null;
                }
                
                if (parsed.cowsFound && parsed.cowsFound.length > GAME_SETTINGS.totalCows) {
                    console.warn('Invalid cow count detected, resetting game');
                    this.resetGame();
                    return null;
                }
                
                if (parsed.cowsFound && parsed.cowsFound.some(id => id < 1 || id > GAME_SETTINGS.totalCows)) {
                    console.warn('Invalid cow ID detected, resetting game');
                    this.resetGame();
                    return null;
                }
                
                if (parsed.hasOwnProperty('trackerMinimized')) {
                    this.state.trackerMinimized = parsed.trackerMinimized;
                } else {
                    this.state.trackerMinimized = true;
                }

                if (parsed.hasOwnProperty('lastFoundCowColor')) {
                    this.state.lastFoundCowColor = parsed.lastFoundCowColor;
                }

                if (parsed.hasOwnProperty('gameCompleted')) {
                    this.state.gameCompleted = parsed.gameCompleted;
                }
                
                return parsed;
            } catch (e) {
                console.warn('Error loading game data, resetting game');
                this.resetGame();
                return null;
            }
        },

        startPageMonitoring: function() {
            if (this.state.pageCheckInterval) {
                clearInterval(this.state.pageCheckInterval);
            }
            
            this.state.pageCheckInterval = setInterval(() => {
                const gameData = this.loadGameData();
                if (gameData && (gameData.active || gameData.gameCompleted)) {
                    const currentPath = window.location.pathname;
                    if (currentPath !== this.state.currentPath) {
                        this.state.currentPath = currentPath;
                        this.handlePageNavigation();
                    }
                }
            }, 100);
        },

        stopPageMonitoring: function() {
            if (this.state.pageCheckInterval) {
                clearInterval(this.state.pageCheckInterval);
                this.state.pageCheckInterval = null;
            }
        },

        createSuccessModal: function() {
            const existingModal = document.getElementById('cowHuntSuccessModal');
            if (existingModal) {
                existingModal.remove();
            }

            const modal = document.createElement('div');
            modal.id = 'cowHuntSuccessModal';
            modal.className = 'cow-hunt-modal';
            modal.style.display = 'block';
            modal.style.zIndex = '2000';

            const modalContent = document.createElement('div');
            modalContent.className = 'cow-hunt-modal-content cow-hunt-success-content';

            const closeBtn = document.createElement('span');
            closeBtn.className = 'cow-hunt-close';
            closeBtn.innerHTML = '&times;';
            closeBtn.onclick = () => this.closeSuccessModal();

            if (window.cowHuntSettings && window.cowHuntSettings.successImage) {
                const successImg = document.createElement('img');
                successImg.src = window.cowHuntSettings.successImage;
                successImg.alt = 'Success Cow Hunt';
                successImg.className = 'cow-hunt-success-image';
                successImg.style.cssText = 'max-width: 100%; height: auto; border-radius: 12px;';
                modalContent.appendChild(successImg);
            } else {
                const successText = document.createElement('p');
                successText.style.textAlign = 'center';
                successText.innerHTML = '🎉 You found all the cows!';
                modalContent.appendChild(successText);
            }

            modalContent.appendChild(closeBtn);
            modal.appendChild(modalContent);
            document.body.appendChild(modal);

            modal.addEventListener('click', (e) => {
                if (e.target === modal) {
                    this.closeSuccessModal();
                }
            });
        },

        closeSuccessModal: function() {
            const modal = document.getElementById('cowHuntSuccessModal');
            if (modal) {
                modal.remove();
            }
            this.resetGame();
        },

        handlePageNavigation: function() {
            const gameData = this.refreshTrackerFromStorage();
            
            if (!gameData || !gameData.active) return;
            
            if (gameData.gameCompleted) {
                this.state.gameCompleted = true;
                this.state.active = false;
                this.createSuccessModal();
                return;
            }
            
            if (!this.state.active || this.state.gameCompleted) return;
            
            this.clearExistingCows();
            
            const positions = this.generateRandomPositions();
            const currentPage = window.location.pathname;
            
            this.state.currentPageCows = positions.map((pos, index) => ({
                id: index + 1,
                position: pos,
                found: this.state.cowsFound.includes(index + 1)
            }));
            
            this.placeCowsForCurrentPage(this.state.currentPageCows, currentPage);
        },

        refreshTrackerFromStorage: function() {
            const gameData = this.loadGameData();
            if (gameData) {
                this.state.cowsFound = gameData.cowsFound || [];
                this.state.lastFoundCowColor = gameData.lastFoundCowColor || null;
                this.state.trackerMinimized = gameData.trackerMinimized !== false;
                this.state.active = gameData.active;
                this.state.gameCompleted = gameData.gameCompleted || false;
                
                if (gameData.active || gameData.gameCompleted) {
                    this.state.cowsFound.forEach(cowId => this.updateCowVisual(cowId));
                    this.updateCounter();
                    this.restoreTrackerState();
                }
            }
            return gameData;
        },

        handleResize: function() {
            if (!this.state.active || this.state.gameCompleted) return;
            
            const currentWidth = window.innerWidth;
            const currentHeight = window.innerHeight;
            
            const widthDiff = Math.abs(currentWidth - this.state.lastWindowSize.width);
            const heightDiff = Math.abs(currentHeight - this.state.lastWindowSize.height);
            
            if (widthDiff < 200 && heightDiff < 200) {
                return;
            }
            
            this.state.lastWindowSize = {
                width: currentWidth,
                height: currentHeight
            };
            
            this.clearExistingCows();
            
            const positions = this.generateRandomPositions();
            const currentPage = window.location.pathname;
            
            this.state.currentPageCows = positions.map((pos, index) => ({
                id: index + 1,
                position: pos,
                found: this.state.cowsFound.includes(index + 1)
            }));
            
            this.placeCowsForCurrentPage(this.state.currentPageCows, currentPage);
        },

        updateUIState: function() {
            const launcher = document.querySelector('.cow-hunt-launcher');
            const launchButton = document.querySelector('.cow-hunt-launch-btn');
            const tracker = document.getElementById('cowHuntTracker');
            
            if (this.state.active) {
                if (launcher) launcher.style.display = 'none';
                if (launchButton) launchButton.style.display = 'none';
            } else {
                if (launcher) launcher.style.display = 'block';
                if (launchButton) launchButton.style.display = 'block';
                if (tracker) tracker.style.display = 'none';
            }
        },

        updateCounter: function() {
            const counter = document.getElementById('cowHuntCounter');
            if (counter) {
                counter.textContent = this.state.cowsFound.length;
            }
            this.updateBubbleColor();
        },

        updateBubbleColor: function() {
            const bubble = document.querySelector('.cow-hunt-bubble-counter');
            const tracker = document.getElementById('cowHuntTracker');
            if (!bubble) return;
        
            const isMinimized = tracker && tracker.classList.contains('cow-hunt-tracker-minimized');
        
            if (isMinimized && this.state.lastFoundCowColor) {
                bubble.style.backgroundColor = this.state.lastFoundCowColor;
                bubble.style.color = 'black';
            } else {
                bubble.style.backgroundColor = 'white';
                bubble.style.color = 'black';
            }
        },

        updateToggleBubble: function() {
            const toggleBubble = document.querySelector('.cow-hunt-toggle-bubble');
            const tracker = document.getElementById('cowHuntTracker');
            
            if (toggleBubble && tracker) {
                const isMinimized = tracker.classList.contains('cow-hunt-tracker-minimized');
                toggleBubble.textContent = isMinimized ? '+' : '-';
            }
        },

        updateCowVisual: function(cowId) {
            const cowItem = document.querySelector(`[data-cow="${cowId}"]`);
            if (cowItem) {
                const placeholder = cowItem.querySelector('.cow-hunt-cow-placeholder, .cow-hunt-cow-placeholder-fallback');
                const found = cowItem.querySelector('.cow-hunt-cow-found, .cow-hunt-cow-found-fallback');
                
                if (placeholder) placeholder.style.display = 'none';
                if (found) found.style.display = 'block';
            }
        },

        showProgressTracker: function() {
            const tracker = document.getElementById('cowHuntTracker');
            if (tracker && this.state.active) {
                tracker.style.display = 'block';
                tracker.classList.remove('cow-hunt-tracker-minimized');
                this.state.trackerMinimized = false;
                this.saveTrackerState();
                this.updateBubbleColor();
                this.updateToggleBubble();
            }
        },

        hideProgressTracker: function() {
            const tracker = document.getElementById('cowHuntTracker');
            if (tracker && this.state.active) {
                tracker.classList.add('cow-hunt-tracker-minimized');
                this.state.trackerMinimized = true;
                this.saveTrackerState();
                this.updateBubbleColor();
                this.updateToggleBubble();
            }
        },

        toggleProgressTracker: function() {
            const tracker = document.getElementById('cowHuntTracker');
            if (tracker && this.state.active) {
                if (tracker.classList.contains('cow-hunt-tracker-minimized')) {
                    this.showProgressTracker();
                } else {
                    this.hideProgressTracker();
                }
                setTimeout(() => {
                    this.updateBubbleColor();
                    this.updateToggleBubble();
                }, 50);
            }
        },

        restoreTrackerState: function() {
            const tracker = document.getElementById('cowHuntTracker');
            if (tracker && this.state.active) {
                tracker.style.display = 'block';
                
                if (this.state.trackerMinimized) {
                    tracker.classList.add('cow-hunt-tracker-minimized');
                } else {
                    tracker.classList.remove('cow-hunt-tracker-minimized');
                }
                
                this.updateCounter();
                this.updateToggleBubble();
                setTimeout(() => this.updateBubbleColor(), 100);
            }
        },

        saveTrackerState: function() {
            try {
                const data = sessionStorage.getItem('cowHuntGame');
                if (data) {
                    const gameData = JSON.parse(data);
                    gameData.trackerMinimized = this.state.trackerMinimized;
                    gameData.lastFoundCowColor = this.state.lastFoundCowColor;
                    sessionStorage.setItem('cowHuntGame', JSON.stringify(gameData));
                }
            } catch (e) {
                console.warn('Error saving tracker state:', e);
            }
        },

        startGame: function() {
            this.state.active = true;
            this.state.cowsFound = [];
            this.state.gameSession = Date.now().toString();
            this.state.lastFoundCowColor = null;
            this.state.gameCompleted = false;
            this.state.currentPath = window.location.pathname;
            
            this.selectRandomPageForCow5();
            
            const existingGame = this.loadGameData();
            if (!existingGame || !existingGame.active) {
                this.state.trackerMinimized = false;
            }
            
            this.clearExistingCows();
            this.placeCows();
            this.resetProgressTracker();
            this.updateUIState();
            this.restoreTrackerState();
            this.updateCounter();
            this.startPageMonitoring();
        },

        placeCows: function() {
            const positions = this.generateRandomPositions();
            const currentPage = window.location.pathname;
            
            this.state.currentPageCows = positions.map((pos, index) => ({
                id: index + 1,
                position: pos,
                found: false
            }));

            this.storeGameData({
                active: this.state.active,
                cowsFound: this.state.cowsFound,
                totalCows: this.state.totalCows,
                gameSession: this.state.gameSession
            });

            this.placeCowsForCurrentPage(this.state.currentPageCows, currentPage);
        },

        placeCowsForCurrentPage: function(cowData, currentPage) {
            this.clearExistingCows();
            
            const pageCows = cowData.filter(cow => {
                if (cow.found || this.state.cowsFound.includes(cow.id)) return false;
                return this.shouldShowCowOnPage(cow.id, currentPage);
            });

            pageCows.forEach(cowInfo => {
                this.createCowElement(cowInfo);
            });
        },

        createCowElement: function(cowInfo) {
            const cow = document.createElement('div');
            cow.className = 'cow-hunt-hidden';
            
            const cowImage = document.createElement('img');
            cowImage.src = this.getCowImageUrl(cowInfo.id);
            cowImage.alt = `Cow #${cowInfo.id}`;
            cowImage.style.cssText = `
                width: 60px;
                height: 60px;
                border-radius: 50%;
                cursor: pointer;
                transition: all 0.3s ease;
                pointer-events: none;
            `;
            
            cow.appendChild(cowImage);
            cow.dataset.cowId = cowInfo.id;
            cow.title = `Cow #${cowInfo.id} - Click to catch!`;
            
            cow.style.top = cowInfo.position.top;
            cow.style.left = cowInfo.position.left;
            
            cow.addEventListener('click', () => this.foundCow(cowInfo.id, cow));
            cow.addEventListener('contextmenu', (e) => e.preventDefault());
            
            document.body.appendChild(cow);
        },

        getCowImageUrl: function(cowId) {
            const cowImages = {
                1: '//5mc2.com/cdn/shop/t/63/assets/cow-1.png?v=38655995428727858421753920459',
                2: '//5mc2.com/cdn/shop/t/63/assets/cow-2.png?v=85585084677621229431753920477',
                3: '//5mc2.com/cdn/shop/t/63/assets/cow-3.png?v=163102830060830066751753920490',
                4: '//5mc2.com/cdn/shop/t/63/assets/cow-4.png?v=65053049691546142761753920505',
                5: '//5mc2.com/cdn/shop/t/63/assets/cow-5.png?v=32631963454332173281753920515'
            };
            
            return cowImages[cowId] || '//5mc2.com/cdn/shop/t/63/assets/cow-1.png?v=38655995428727858421753920459';
        },

        foundCow: function(cowId, cowElement) {
            if (!this.state.active || this.state.cowsFound.includes(cowId)) return;
        
            const currentPage = window.location.pathname;
            if (!this.shouldShowCowOnPage(cowId, currentPage)) {
                console.warn('Invalid cow click detected');
                return;
            }
            
            this.state.cowsFound.push(cowId);
            this.state.lastFoundCowColor = COW_COLORS[cowId] || '#FFFFFF';
        
            this.storeGameData({
                active: this.state.active,
                cowsFound: this.state.cowsFound,
                totalCows: this.state.totalCows,
                gameSession: this.state.gameSession,
                lastFoundCowColor: this.state.lastFoundCowColor
            });
        
            const tracker = document.getElementById('cowHuntTracker');
            if (tracker) {
                if (this.state.trackerMinimized) {
                    tracker.classList.add('cow-hunt-tracker-minimized');
                } else {
                    tracker.classList.remove('cow-hunt-tracker-minimized');
                }
            }
        
            setTimeout(() => this.updateBubbleColor(), 150);
        
            cowElement.style.transform = 'scale(1.5)';
            cowElement.style.opacity = '0';
            setTimeout(() => cowElement.remove(), 300);
        
            this.updateCowVisual(cowId);
            this.updateCounter();
            this.showFoundEffect(cowElement);
        
            if (this.state.cowsFound.length === this.state.totalCows) {
                setTimeout(() => this.completeGame(), 500);
            }
        },

        showFoundEffect: function(cowElement) {
            const rect = cowElement.getBoundingClientRect();
            const effect = document.createElement('img');

            effect.src = "//5mc2.com/cdn/shop/t/63/assets/cow-found-effect.png?v=116492033379958305201753920592";
            
            effect.style.cssText = `
                position: fixed;
                top: ${rect.top - 25}px;
                left: ${rect.left + rect.width - 15}px;
                width: 40px;
                height: 40px;
                pointer-events: none;
                z-index: 2000;
                transform: rotate(10deg);
            `;
        
            document.body.appendChild(effect);
            setTimeout(() => effect.remove(), 300);
        },

        resetProgressTracker: function() {
            document.querySelectorAll('.cow-hunt-cow-item').forEach(item => {
                const placeholder = item.querySelector('.cow-hunt-cow-placeholder, .cow-hunt-cow-placeholder-fallback');
                const found = item.querySelector('.cow-hunt-cow-found, .cow-hunt-cow-found-fallback');
                
                if (placeholder) placeholder.style.display = 'block';
                if (found) found.style.display = 'none';
            });
            
            this.updateCounter();
        },

        completeGame: function() {
            this.state.active = false;
            this.state.gameCompleted = true;
            this.stopPageMonitoring();
            
            this.storeGameData({
                active: false,
                cowsFound: this.state.cowsFound,
                totalCows: this.state.totalCows,
                gameSession: this.state.gameSession,
                gameCompleted: true
            });
            
            document.getElementById('cowHuntTracker').style.display = 'none';
            this.createSuccessModal();
        },

        clearExistingCows: function() {
            document.querySelectorAll('.cow-hunt-hidden').forEach(cow => cow.remove());
        },

        resetGame: function() {
            this.state.active = false;
            this.state.cowsFound = [];
            this.state.trackerMinimized = true;
            this.state.lastFoundCowColor = null;
            this.state.cow5SelectedPage = null;
            this.state.currentPageCows = [];
            this.state.gameCompleted = false;
            this.clearExistingCows();
            this.updateUIState();
            this.stopPageMonitoring();
            
            try {
                sessionStorage.removeItem('cowHuntGame');
            } catch (e) {
                console.warn('Could not clear session storage');
            }
        },

        init: function() {
            this.state.totalCows = GAME_SETTINGS.totalCows;
            this.state.currentPath = window.location.pathname;
            
            this.state.lastWindowSize = {
                width: window.innerWidth,
                height: window.innerHeight
            };
            
            const gameData = this.loadGameData();
            if (gameData && gameData.active) {
                this.state.active = gameData.active;
                this.state.cowsFound = gameData.cowsFound || [];
                this.state.totalCows = gameData.totalCows || GAME_SETTINGS.totalCows;
                this.state.gameSession = gameData.gameSession;
                this.state.gameCompleted = gameData.gameCompleted || false;
                this.selectRandomPageForCow5();
                
                if (gameData.lastFoundCowColor) {
                    this.state.lastFoundCowColor = gameData.lastFoundCowColor;
                } else if (this.state.cowsFound.length > 0) {
                    const lastFoundCowId = this.state.cowsFound[this.state.cowsFound.length - 1];
                    this.state.lastFoundCowColor = COW_COLORS[lastFoundCowId] || '#FFFFFF';
                }
                
                if (this.state.active) {
                    const positions = this.generateRandomPositions();
                    this.state.currentPageCows = positions.map((pos, index) => ({
                        id: index + 1,
                        position: pos,
                        found: this.state.cowsFound.includes(index + 1)
                    }));
                    
                    this.placeCowsForCurrentPage(this.state.currentPageCows, window.location.pathname);
                    this.state.cowsFound.forEach(cowId => this.updateCowVisual(cowId));
                    this.updateCounter();
                    this.startPageMonitoring();
                }
            }
            
            if (gameData && gameData.gameCompleted) {
                this.state.gameCompleted = true;
                this.createSuccessModal();
            }
            
            this.updateUIState();
            
            if (this.state.active && !this.state.gameCompleted) {
                this.restoreTrackerState();
            }

            window.addEventListener('pageshow', (e) => {
                const gameData = this.loadGameData();
                if (e.persisted && gameData && (gameData.active || gameData.gameCompleted)) {
                    setTimeout(() => {
                        this.handlePageNavigation();
                    }, 100);
                }
            });

            window.addEventListener('popstate', () => {
                setTimeout(() => {
                    this.handlePageNavigation();
                }, 100);
            });

            window.addEventListener('hashchange', () => {
                setTimeout(() => {
                    this.handlePageNavigation();
                }, 100);
            });

            let resizeTimeout;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    this.handleResize();
                }, 300);
            });

            const toggleBubble = document.querySelector('.cow-hunt-toggle-bubble');
            if (toggleBubble) {
                toggleBubble.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.toggleProgressTracker();
                });
            }
        }
    };

    const SecurityModule = {
        init: function() {
            document.addEventListener('contextmenu', (e) => {
                if (e.target.classList.contains('cow-hunt-hidden') ||
                    e.target.closest('.cow-hunt-modal') ||
                    e.target.closest('.cow-hunt-tracker-redesigned')) {
                    e.preventDefault();
                }
            });

            Object.defineProperty(window, 'CowHuntGame', {
                value: Object.freeze(CowHuntGame),
                writable: false,
                configurable: false
            });
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            CowHuntGame.init();
            SecurityModule.init();
        });
    } else {
        CowHuntGame.init();
        SecurityModule.init();
    }

})();
            
            const currentWidth = window.innerWidth;
            const currentHeight = window.innerHeight;
            
            const widthDiff = Math.abs(currentWidth - this.state.lastWindowSize.width);
            const heightDiff = Math.abs(currentHeight - this.state.lastWindowSize.height);
            
            if (widthDiff < 200 && heightDiff < 200) {
                return;
            }
            
            this.state.lastWindowSize = {
                width: currentWidth,
                height: currentHeight
            };
            
            this.clearExistingCows();
            
            const positions = this.generateRandomPositions();
            const currentPage = window.location.pathname;
            
            this.state.currentPageCows = positions.map((pos, index) => ({
                id: index + 1,
                position: pos,
                found: this.state.cowsFound.includes(index + 1)
            }));
            
            this.placeCowsForCurrentPage(this.state.currentPageCows, currentPage);
        },

        updateUIState: function() {
            const launcher = document.querySelector('.cow-hunt-launcher');
            const launchButton = document.querySelector('.cow-hunt-launch-btn');
            const tracker = document.getElementById('cowHuntTracker');
            
            if (this.state.active) {
                if (launcher) launcher.style.display = 'none';
                if (launchButton) launchButton.style.display = 'none';
            } else {
                if (launcher) launcher.style.display = 'block';
                if (launchButton) launchButton.style.display = 'block';
                if (tracker) tracker.style.display = 'none';
            }
        },

        updateCounter: function() {
            const counter = document.getElementById('cowHuntCounter');
            if (counter) {
                counter.textContent = this.state.cowsFound.length;
            }
            this.updateBubbleColor();
        },

        updateBubbleColor: function() {
            const bubble = document.querySelector('.cow-hunt-bubble-counter');
            const tracker = document.getElementById('cowHuntTracker');
            if (!bubble) return;
        
            const isMinimized = tracker && tracker.classList.contains('cow-hunt-tracker-minimized');
        
            if (isMinimized && this.state.lastFoundCowColor) {
                bubble.style.backgroundColor = this.state.lastFoundCowColor;
                bubble.style.color = 'black';
            } else {
                bubble.style.backgroundColor = 'white';
                bubble.style.color = 'black';
            }
        },

        updateToggleBubble: function() {
            const toggleBubble = document.querySelector('.cow-hunt-toggle-bubble');
            const tracker = document.getElementById('cowHuntTracker');
            
            if (toggleBubble && tracker) {
                const isMinimized = tracker.classList.contains('cow-hunt-tracker-minimized');
                toggleBubble.textContent = isMinimized ? '+' : '-';
            }
        },

        updateCowVisual: function(cowId) {
            const cowItem = document.querySelector(`[data-cow="${cowId}"]`);
            if (cowItem) {
                const placeholder = cowItem.querySelector('.cow-hunt-cow-placeholder, .cow-hunt-cow-placeholder-fallback');
                const found = cowItem.querySelector('.cow-hunt-cow-found, .cow-hunt-cow-found-fallback');
                
                if (placeholder) placeholder.style.display = 'none';
                if (found) found.style.display = 'block';
            }
        },

        showProgressTracker: function() {
            const tracker = document.getElementById('cowHuntTracker');
            if (tracker && this.state.active) {
                tracker.style.display = 'block';
                tracker.classList.remove('cow-hunt-tracker-minimized');
                this.state.trackerMinimized = false;
                this.saveTrackerState();
                this.updateBubbleColor();
                this.updateToggleBubble();
            }
        },

        hideProgressTracker: function() {
            const tracker = document.getElementById('cowHuntTracker');
            if (tracker && this.state.active) {
                tracker.classList.add('cow-hunt-tracker-minimized');
                this.state.trackerMinimized = true;
                this.saveTrackerState();
                this.updateBubbleColor();
                this.updateToggleBubble();
            }
        },

        toggleProgressTracker: function() {
            const tracker = document.getElementById('cowHuntTracker');
            if (tracker && this.state.active) {
                if (tracker.classList.contains('cow-hunt-tracker-minimized')) {
                    this.showProgressTracker();
                } else {
                    this.hideProgressTracker();
                }
                setTimeout(() => {
                    this.updateBubbleColor();
                    this.updateToggleBubble();
                }, 50);
            }
        },

        restoreTrackerState: function() {
            const tracker = document.getElementById('cowHuntTracker');
            if (tracker && this.state.active) {
                tracker.style.display = 'block';
                
                if (this.state.trackerMinimized) {
                    tracker.classList.add('cow-hunt-tracker-minimized');
                } else {
                    tracker.classList.remove('cow-hunt-tracker-minimized');
                }
                
                this.updateCounter();
                this.updateToggleBubble();
                setTimeout(() => this.updateBubbleColor(), 100);
            }
        },

        saveTrackerState: function() {
            try {
                const data = sessionStorage.getItem('cowHuntGame');
                if (data) {
                    const gameData = JSON.parse(data);
                    gameData.trackerMinimized = this.state.trackerMinimized;
                    gameData.lastFoundCowColor = this.state.lastFoundCowColor;
                    sessionStorage.setItem('cowHuntGame', JSON.stringify(gameData));
                }
            } catch (e) {
                console.warn('Error saving tracker state:', e);
            }
        },

        startGame: function() {
            this.state.active = true;
            this.state.cowsFound = [];
            this.state.gameSession = Date.now().toString();
            this.state.lastFoundCowColor = null;
            this.state.gameCompleted = false;
            this.state.currentPath = window.location.pathname;
            
            this.selectRandomPageForCow5();
            
            const existingGame = this.loadGameData();
            if (!existingGame || !existingGame.active) {
                this.state.trackerMinimized = false;
            }
            
            this.clearExistingCows();
            this.placeCows();
            this.resetProgressTracker();
            this.updateUIState();
            this.restoreTrackerState();
            this.updateCounter();
            this.startPageMonitoring();
        },

        placeCows: function() {
            const positions = this.generateRandomPositions();
            const currentPage = window.location.pathname;
            
            this.state.currentPageCows = positions.map((pos, index) => ({
                id: index + 1,
                position: pos,
                found: false
            }));

            this.storeGameData({
                active: this.state.active,
                cowsFound: this.state.cowsFound,
                totalCows: this.state.totalCows,
                gameSession: this.state.gameSession
            });

            this.placeCowsForCurrentPage(this.state.currentPageCows, currentPage);
        },

        placeCowsForCurrentPage: function(cowData, currentPage) {
            this.clearExistingCows();
            
            const pageCows = cowData.filter(cow => {
                if (cow.found || this.state.cowsFound.includes(cow.id)) return false;
                return this.shouldShowCowOnPage(cow.id, currentPage);
            });

            pageCows.forEach(cowInfo => {
                this.createCowElement(cowInfo);
            });
        },

        createCowElement: function(cowInfo) {
            const cow = document.createElement('div');
            cow.className = 'cow-hunt-hidden';
            
            const cowImage = document.createElement('img');
            cowImage.src = this.getCowImageUrl(cowInfo.id);
            cowImage.alt = `Cow #${cowInfo.id}`;
            cowImage.style.cssText = `
                width: 60px;
                height: 60px;
                border-radius: 50%;
                cursor: pointer;
                transition: all 0.3s ease;
                pointer-events: none;
            `;
            
            cow.appendChild(cowImage);
            cow.dataset.cowId = cowInfo.id;
            cow.title = `Cow #${cowInfo.id} - Click to catch!`;
            
            cow.style.top = cowInfo.position.top;
            cow.style.left = cowInfo.position.left;
            
            cow.addEventListener('click', () => this.foundCow(cowInfo.id, cow));
            cow.addEventListener('contextmenu', (e) => e.preventDefault());
            
            document.body.appendChild(cow);
        },

        getCowImageUrl: function(cowId) {
            const cowImages = {
                1: '//5mc2.com/cdn/shop/t/63/assets/cow-1.png?v=38655995428727858421753920459',
                2: '//5mc2.com/cdn/shop/t/63/assets/cow-2.png?v=85585084677621229431753920477',
                3: '//5mc2.com/cdn/shop/t/63/assets/cow-3.png?v=163102830060830066751753920490',
                4: '//5mc2.com/cdn/shop/t/63/assets/cow-4.png?v=65053049691546142761753920505',
                5: '//5mc2.com/cdn/shop/t/63/assets/cow-5.png?v=32631963454332173281753920515'
            };
            
            return cowImages[cowId] || '//5mc2.com/cdn/shop/t/63/assets/cow-1.png?v=38655995428727858421753920459';
        },

        foundCow: function(cowId, cowElement) {
            if (!this.state.active || this.state.cowsFound.includes(cowId)) return;
        
            const currentPage = window.location.pathname;
            if (!this.shouldShowCowOnPage(cowId, currentPage)) {
                console.warn('Invalid cow click detected');
                return;
            }
            
            this.state.cowsFound.push(cowId);
            this.state.lastFoundCowColor = COW_COLORS[cowId] || '#FFFFFF';
        
            this.storeGameData({
                active: this.state.active,
                cowsFound: this.state.cowsFound,
                totalCows: this.state.totalCows,
                gameSession: this.state.gameSession,
                lastFoundCowColor: this.state.lastFoundCowColor
            });
        
            const tracker = document.getElementById('cowHuntTracker');
            if (tracker) {
                if (this.state.trackerMinimized) {
                    tracker.classList.add('cow-hunt-tracker-minimized');
                } else {
                    tracker.classList.remove('cow-hunt-tracker-minimized');
                }
            }
        
            setTimeout(() => this.updateBubbleColor(), 150);
        
            cowElement.style.transform = 'scale(1.5)';
            cowElement.style.opacity = '0';
            setTimeout(() => cowElement.remove(), 300);
        
            this.updateCowVisual(cowId);
            this.updateCounter();
            this.showFoundEffect(cowElement);
        
            if (this.state.cowsFound.length === this.state.totalCows) {
                setTimeout(() => this.completeGame(), 500);
            }
        },

        showFoundEffect: function(cowElement) {
            const rect = cowElement.getBoundingClientRect();
            const effect = document.createElement('img');

            effect.src = "//5mc2.com/cdn/shop/t/63/assets/cow-found-effect.png?v=116492033379958305201753920592";
            
            effect.style.cssText = `
                position: fixed;
                top: ${rect.top - 25}px;
                left: ${rect.left + rect.width - 15}px;
                width: 40px;
                height: 40px;
                pointer-events: none;
                z-index: 2000;
                transform: rotate(10deg);
            `;
        
            document.body.appendChild(effect);
            setTimeout(() => effect.remove(), 300);
        },

        resetProgressTracker: function() {
            document.querySelectorAll('.cow-hunt-cow-item').forEach(item => {
                const placeholder = item.querySelector('.cow-hunt-cow-placeholder, .cow-hunt-cow-placeholder-fallback');
                const found = item.querySelector('.cow-hunt-cow-found, .cow-hunt-cow-found-fallback');
                
                if (placeholder) placeholder.style.display = 'block';
                if (found) found.style.display = 'none';
            });
            
            this.updateCounter();
        },

        completeGame: function() {
            this.state.active = false;
            this.state.gameCompleted = true;
            this.stopPageMonitoring();
            
            this.storeGameData({
                active: false,
                cowsFound: this.state.cowsFound,
                totalCows: this.state.totalCows,
                gameSession: this.state.gameSession,
                gameCompleted: true
            });
            
            document.getElementById('cowHuntTracker').style.display = 'none';
            this.createSuccessModal();
        },

        clearExistingCows: function() {
            document.querySelectorAll('.cow-hunt-hidden').forEach(cow => cow.remove());
        },

        resetGame: function() {
            this.state.active = false;
            this.state.cowsFound = [];
            this.state.trackerMinimized = true;
            this.state.lastFoundCowColor = null;
            this.state.cow5SelectedPage = null;
            this.state.currentPageCows = [];
            this.state.gameCompleted = false;
            this.clearExistingCows();
            this.updateUIState();
            this.stopPageMonitoring();
            
            try {
                sessionStorage.removeItem('cowHuntGame');
            } catch (e) {
                console.warn('Could not clear session storage');
            }
        },

        init: function() {
            this.state.totalCows = GAME_SETTINGS.totalCows;
            this.state.currentPath = window.location.pathname;
            
            this.state.lastWindowSize = {
                width: window.innerWidth,
                height: window.innerHeight
            };
            
            const gameData = this.loadGameData();
            if (gameData && gameData.active) {
                this.state.active = gameData.active;
                this.state.cowsFound = gameData.cowsFound || [];
                this.state.totalCows = gameData.totalCows || GAME_SETTINGS.totalCows;
                this.state.gameSession = gameData.gameSession;
                this.state.gameCompleted = gameData.gameCompleted || false;
                this.selectRandomPageForCow5();
                
                if (gameData.lastFoundCowColor) {
                    this.state.lastFoundCowColor = gameData.lastFoundCowColor;
                } else if (this.state.cowsFound.length > 0) {
                    const lastFoundCowId = this.state.cowsFound[this.state.cowsFound.length - 1];
                    this.state.lastFoundCowColor = COW_COLORS[lastFoundCowId] || '#FFFFFF';
                }
                
                if (this.state.active) {
                    const positions = this.generateRandomPositions();
                    this.state.currentPageCows = positions.map((pos, index) => ({
                        id: index + 1,
                        position: pos,
                        found: this.state.cowsFound.includes(index + 1)
                    }));
                    
                    this.placeCowsForCurrentPage(this.state.currentPageCows, window.location.pathname);
                    this.state.cowsFound.forEach(cowId => this.updateCowVisual(cowId));
                    this.updateCounter();
                    this.startPageMonitoring();
                }
            }
            
            if (gameData && gameData.gameCompleted) {
                this.state.gameCompleted = true;
                this.createSuccessModal();
            }
            
            this.updateUIState();
            
            if (this.state.active && !this.state.gameCompleted) {
                this.restoreTrackerState();
            }

            window.addEventListener('pageshow', (e) => {
                const gameData = this.loadGameData();
                if (e.persisted && gameData && (gameData.active || gameData.gameCompleted)) {
                    setTimeout(() => {
                        this.handlePageNavigation();
                    }, 100);
                }
            });

            window.addEventListener('popstate', () => {
                setTimeout(() => {
                    this.handlePageNavigation();
                }, 100);
            });

            window.addEventListener('hashchange', () => {
                setTimeout(() => {
                    this.handlePageNavigation();
                }, 100);
            });

            let resizeTimeout;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    this.handleResize();
                }, 300);
            });

            const toggleBubble = document.querySelector('.cow-hunt-toggle-bubble');
            if (toggleBubble) {
                toggleBubble.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.toggleProgressTracker();
                });
            }
        }
    };

    const SecurityModule = {
        init: function() {
            document.addEventListener('contextmenu', (e) => {
                if (e.target.classList.contains('cow-hunt-hidden') ||
                    e.target.closest('.cow-hunt-modal') ||
                    e.target.closest('.cow-hunt-tracker-redesigned')) {
                    e.preventDefault();
                }
            });

            Object.defineProperty(window, 'CowHuntGame', {
                value: Object.freeze(CowHuntGame),
                writable: false,
                configurable: false
            });
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            CowHuntGame.init();
            SecurityModule.init();
        });
    } else {
        CowHuntGame.init();
        SecurityModule.init();
    }

})();